const DEFAULT_OPTIONS = {
  maxDim: 720,
  quality: 0.82,
  mimeType: "image/jpeg",
  timeoutMs: 15000,
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const loadImage = (src, timeoutMs) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      img.src = "";
      reject(new Error("Image load timeout"));
    }, timeoutMs);

    img.onload = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(err);
    };
    img.src = src;
  });

const drawToCanvas = (source, sourceWidth, sourceHeight, maxDim) => {
  const scale = Math.min(1, maxDim / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, width, height);

  return canvas;
};

/**
 * Resize a canvas in place and return a compressed data URL.
 * Fast path for sources that already have a canvas (e.g. webcam frames) —
 * avoids a second Image decode + canvas allocation.
 */
export const resizeCanvasToDataUrl = (canvas, options = {}) => {
  const { maxDim, quality, mimeType } = { ...DEFAULT_OPTIONS, ...options };
  const resized = drawToCanvas(canvas, canvas.width, canvas.height, maxDim);
  return resized.toDataURL(mimeType, quality);
};

/**
 * Compress and downscale an image before upload.
 *
 * Accepts either a File/Blob (e.g. from <input type="file">) or a data URL
 * string (e.g. from canvas.toDataURL / camera capture) and returns a
 * compressed JPEG data URL ready to be sent to the backend.
 *
 * For File input, EXIF orientation is respected via createImageBitmap when
 * available — without this, phone photos often upload sideways.
 *
 * Has a hard timeout on image decoding so mobile browsers cannot hang the
 * upload flow forever. If compression fails, the original bytes are returned
 * so the user still gets an upload attempt.
 */
export default async function compressImage(input, options = {}) {
  const { maxDim, quality, mimeType, timeoutMs } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  try {
    if (input instanceof Blob) {
      if (typeof createImageBitmap === "function") {
        try {
          const bitmap = await createImageBitmap(input, {
            imageOrientation: "from-image",
          });
          const canvas = drawToCanvas(
            bitmap,
            bitmap.width,
            bitmap.height,
            maxDim
          );
          bitmap.close?.();
          return canvas.toDataURL(mimeType, quality);
        } catch {
          // fall through to the <img> + objectURL path below
        }
      }

      let objectUrl = null;
      try {
        objectUrl = URL.createObjectURL(input);
        const img = await loadImage(objectUrl, timeoutMs);
        const canvas = drawToCanvas(
          img,
          img.naturalWidth,
          img.naturalHeight,
          maxDim
        );
        return canvas.toDataURL(mimeType, quality);
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    }

    if (typeof input === "string") {
      const img = await loadImage(input, timeoutMs);
      const canvas = drawToCanvas(
        img,
        img.naturalWidth,
        img.naturalHeight,
        maxDim
      );
      return canvas.toDataURL(mimeType, quality);
    }

    throw new Error("Unsupported input type for compressImage");
  } catch (err) {
    if (typeof input === "string") return input;
    try {
      return await readFileAsDataUrl(input);
    } catch {
      throw err;
    }
  }
}
